/* eslint-disable react/no-multi-comp */
import { useAtomValue } from "jotai";
import { useHydrateAtoms } from "jotai/utils";
import Cookies from "js-cookie";
import isString from "lodash/isString";
import type { NextPageContext, PageProps } from "next";
import { useRouter } from "next/router";
import type { NextRouter } from "next/router";
import { type FunctionComponent, memo, useMemo } from "react";
import { graphql, useFragment, useLazyLoadQuery, usePreloadedQuery } from "react-relay";
import type { RelayComponent, RelayComponentWithLayout } from "relay-nextjs";
import { withRelay } from "relay-nextjs";

import { config as configData } from "shared/data";
import { serverConfig } from "shared/data/configs/server";
import { parseEdition } from "shared/lib/edition";

import { useTopBannerAdSlot } from "scmp-app/components/advertisement/ad-slots/top-banner-ad-slot/hooks";
import type { AppBarVariant } from "scmp-app/components/app-bar/types";
import type { Application } from "scmp-app/components/app-initializer/helpers";
import { ArticleLinkingData } from "scmp-app/components/article/article-linking-data";
import { ArticleList } from "scmp-app/components/article/article-list";
import { DefaultCustomContents } from "scmp-app/components/article/article-render/consts";
import { useArticleBaseAdvertisingInfo } from "scmp-app/components/article/article-render/hooks";
import { BotOnlyLiveArticleContext } from "scmp-app/components/article/article-render/live-article/bot-only-live-article-context";
import { PlusCustomContents } from "scmp-app/components/article/article-render/plus/consts";
import { StyleCustomContents } from "scmp-app/components/article/article-render/style-article/consts";
import { ArticleRenderType } from "scmp-app/components/article/article-render/types";
import { ArticleSchema } from "scmp-app/components/article/article-schema";
import { ArticleSeo } from "scmp-app/components/article/article-seo";
import { ContentShareWidgetDrawer } from "scmp-app/components/content/content-share-widget-drawer";
import { FallbackLoading } from "scmp-app/components/fallback-loading";
import { VideoSchema } from "scmp-app/components/video/video-schema";
import { VideoSeo } from "scmp-app/components/video/video-seo";
import { fetchAiSeoSuggestion } from "scmp-app/lib/ai-seo/apis";
import type { AiSeoOverride } from "scmp-app/lib/ai-seo/types";
import { currentArticleAtom, currentItemAtom, getCurrentItemData } from "scmp-app/lib/current-item";
import { createClientEnvironment } from "scmp-app/lib/relay/environment.client";
import type { articlePageLiveArticleBodyContent$key } from "scmp-app/queries/__generated__/articlePageLiveArticleBodyContent.graphql";
import type {
  articlePageQuery,
  BodyFormatTypeEnum,
} from "scmp-app/queries/__generated__/articlePageQuery.graphql";

import { decryptedShare } from "./helpers";
import {
  useAmpRedirect,
  useArticleAcknowledgementGateOverlay,
  useArticlePageAdvertisementConfig,
  useArticlePageArticleSpeechConfig,
  useArticlePageFloatingButtonConfig,
  useTrackContentInterest,
  useTrackPageView,
} from "./hooks";
import { Article } from "./styles";

// Varnish injects x-is-bot: 1 for verified bot traffic (and hashes on it,
// so bot and human HTML are cached separately). req only exists on
// NextPageContext (SSR); on client navigation (NextRouter) there is no
// server request, and x-is-bot is absent by definition.
const isBotRequest = (context: NextPageContext | NextRouter) =>
  "req" in context ? context.req?.headers["x-is-bot"] === "1" : false;

export const query = graphql`
  query articlePageQuery(
    $appConfigEntityId: String!
    $bodyType: BodyFormatTypeEnum
    $customContents: [CustomContentInput]
    $entityId: String!
    $entityUuid: String!
    $includeLiveContents: Boolean!
    $isMagazinesStyle: Boolean!
    $isVideo: Boolean!
  ) {
    article(filter: { entityId: $entityId }) @skip(if: $isVideo) {
      ...articleBaseAdvertisingInfoArticle
      ...articleListContent @arguments(customContents: $customContents, bodyType: $bodyType)
      ...articleSeoArticle @arguments(customContents: $customContents, bodyType: $bodyType)
      ...hooksArticleAcknowledgementGateOverlayArticle
      ...hooksTrackCurrentItemBase @arguments(customContents: $customContents, bodyType: $bodyType)
      ...hooksTrackPageViewArticle
      ...hooksContentInterestArticle
      ...articleLinkingDataContent
      ...hooksAmpRedirectArticle
      entityId
    }
    videoContent: content(contentType: VIDEO, filter: { entityId: $entityId })
      @include(if: $isVideo) {
      ...articleBaseAdvertisingInfoArticle
      ...articleListContent @arguments(customContents: $customContents, bodyType: $bodyType)
      ...hooksArticleAcknowledgementGateOverlayArticle
      ...hooksTrackCurrentItemBase @arguments(customContents: $customContents, bodyType: $bodyType)
      ...hooksTrackPageViewArticle
      ...hooksContentInterestArticle
      ...articleLinkingDataContent
      entityId
      ... on Video {
        ...videoSchemaVideo
        ...videoSeoVideo
      }
    }
    ...articleSchemaContent
      @arguments(entityId: $entityId, entityUuid: $entityUuid, bodyType: $bodyType)
      @skip(if: $isVideo)
    ...articlePageLiveArticleBodyContent
      @alias
      @arguments(entityUuid: $entityUuid)
      @include(if: $includeLiveContents)
    ...hooksArticlePageAdvertisementConfigArticle @arguments(appConfigEntityId: $appConfigEntityId)
    ...hooksArticlePageArticleSpeechConfigArticle
    ...hooksArticlePageFloatingButtonConfigArticle
    ...headerOnelineMenuStyleQuery @include(if: $isMagazinesStyle)
  }
`;

const ArticlePage: RelayComponent<
  PageProps & { aiSeoOverride?: AiSeoOverride },
  articlePageQuery
> = ({ aiSeoOverride, appBarConfiguration, preloadedQuery, shareContentUnlocked }) => {
  const queryReference = usePreloadedQuery(query, preloadedQuery);
  const content = queryReference.article ?? queryReference.videoContent;
  const isVideo = !queryReference.article && !!queryReference.videoContent;

  // Bot-only SSR for live contents: only bot requests have this aliased
  // spread populated (the @include(if: $includeLiveContents) gate in the page
  // query is driven by the x-is-bot request header in variablesFromContext).
  // Humans get null here and render live contents fully client-side.
  // The @alias spread lands on the query root as a nullable field that
  // carries exactly this fragment's $fragmentSpreads; Relay 20's useFragment
  // typing cannot infer a nullable key from it, so this single cast is the
  // contract.
  const liveArticleBodyData = useFragment<articlePageLiveArticleBodyContent$key>(
    graphql`
      fragment articlePageLiveArticleBodyContent on Query
      @argumentDefinitions(entityUuid: { type: "String!" }) {
        liveArticle(filter: { entityUuid: $entityUuid }) {
          ...ssrLiveContentsPreviewLiveArticle
        }
      }
    `,
    (queryReference.articlePageLiveArticleBodyContent ??
      null) as articlePageLiveArticleBodyContent$key | null,
  );

  const botOnlyLiveArticle = liveArticleBodyData?.liveArticle ?? null;

  const advertisingInfo = useArticleBaseAdvertisingInfo(content!);
  const currentArticle = useAtomValue(currentArticleAtom);
  const { isAcknowledgementRequired, renderAcknowledgementGateOverlay } =
    useArticleAcknowledgementGateOverlay(content!);

  useAmpRedirect(queryReference.article ?? null);
  useTrackPageView(currentArticle ?? content!, shareContentUnlocked);
  useTrackContentInterest(currentArticle ?? content!);
  useTopBannerAdSlot({
    desktop: null,
    mobile: appBarConfiguration?.hasMobileAd
      ? {
          ...advertisingInfo,
          adUnit: "m_banner3",
          autoRefreshOptions: {
            scrollBackRefreshOptions: {
              slotArticleEntityId: content?.entityId ?? "",
            },
          },
        }
      : null,
  });
  useArticlePageAdvertisementConfig({
    reference: queryReference,
  });
  useArticlePageArticleSpeechConfig({
    reference: queryReference,
  });
  useArticlePageFloatingButtonConfig({
    reference: queryReference,
  });

  if (!content) return null;

  return (
    <BotOnlyLiveArticleContext.Provider value={botOnlyLiveArticle}>
      {isVideo ? (
        <VideoSeo reference={queryReference.videoContent} />
      ) : (
        <ArticleSeo
          aiSeoOverride={aiSeoOverride}
          reference={currentArticle ?? queryReference.article!}
        />
      )}
      <ContentShareWidgetDrawer />
      {!isVideo && <ArticleLinkingData reference={queryReference.article!} />}
      {!isVideo && <ArticleSchema reference={queryReference} />}
      {isVideo && <VideoSchema reference={queryReference.videoContent} />}
      {renderAcknowledgementGateOverlay()}
      {/* TODO: workaround for fixing https://scmp-product-tech.atlassian.net/browse/SCMPWEB-3430, third party should not relied on the application level */}
      <Article $isShowArticle={!isAcknowledgementRequired} className="article-list">
        <List reference={content} shareContentUnlocked={shareContentUnlocked} />
      </Article>
    </BotOnlyLiveArticleContext.Provider>
  );
};

const List = memo(ArticleList);

ArticlePage.displayName = "ArticlePage";

const getPageProps = (context: NextPageContext) => {
  const appBarVariant = context.query.appBarVariant as AppBarVariant;
  const application = context.query.application as Application;
  const renderType = context.query.renderType as ArticleRenderType | undefined;

  const shareContentUnlocked =
    isString(context.query.entityId) && isString(context.query?.share)
      ? decryptedShare(context)
      : undefined;

  const pageProps: PageProps = {
    animateKey: `article-${context.query.entityId?.toString() ?? ""}`,
    appBarConfiguration: {
      hasMobileAd: appBarVariant === "scmp/generic-light-with-banner-ad",
      variant: appBarVariant,
    },
    appInitializerConfiguration: {
      application,
    },
    contentConfiguration: {
      // check for article is generic and use the new padding config
      responsivePadding: (() => {
        switch (renderType) {
          case ArticleRenderType.Generic:
          case ArticleRenderType.Video:
            return {
              desktopUp: 12,
              mobileUp: 4,
              tabletUp: 12,
            };
          default:
            return {
              desktopUp: 32,
              mediumDesktopUp: 52,
              tabletUp: 12,
            };
        }
      })(),
    },
    initEdition: parseEdition(context.req?.cookies?.scmp_edition ?? Cookies.get("scmp_edition")),
    shareContentUnlocked,
  };

  if (appBarVariant === "scmp/magazines-style") {
    pageProps.pageConfiguration = {
      ...pageProps.pageConfiguration,
      responsivePadding: {
        desktopUp: 40,
        mobileUp: 16,
        tabletUp: 20,
      },
    };
    pageProps.contentConfiguration = {
      ...pageProps.contentConfiguration,
      responsivePadding: {
        desktopUp: 135,
        mobileUp: 24,
        tabletUp: 70,
      },
    };
  }

  return pageProps;
};

const getVariables = (context: NextPageContext | NextRouter) => {
  const appBarVariant = context.query.appBarVariant as AppBarVariant;
  const isVideo = context.query.isVideo === "true";
  const renderType = context.query.renderType as ArticleRenderType | undefined;
  const isBot = isBotRequest(context);

  // Bot-only SSR for live contents: fetch liveArticle data server-side for
  // verified bot requests on live articles. Humans render live contents
  // fully client-side, so gated content never leaks into their HTML. Varnish
  // hashes on x-is-bot, so bot and human HTML are cached separately.
  const includeLiveContents =
    isBot && renderType === ArticleRenderType.Live && isString(context.query.entityUuid);

  const getCustomContents = () => {
    switch (appBarVariant) {
      case "scmp/magazines-style":
        return StyleCustomContents;
      case "scmp/plus":
        return PlusCustomContents;
      default:
        return DefaultCustomContents;
    }
  };

  const bodyType: BodyFormatTypeEnum =
    appBarVariant === "scmp/magazines-style" ? "PARAGRAPHANDMEDIA" : "PARAGRAPH";

  return isString(context.query.entityId) && (isString(context.query.entityUuid) || isVideo)
    ? {
        appConfigEntityId: configData.advertisement.configId,
        bodyType,
        customContents: getCustomContents(),
        entityId: context.query.entityId,
        entityUuid: isString(context.query.entityUuid) ? context.query.entityUuid : "",
        includeLiveContents,
        isMagazinesStyle: appBarVariant === "scmp/magazines-style",
        isVideo,
      }
    : {
        appConfigEntityId: configData.advertisement.configId,
        bodyType,
        customContents: [],
        entityId: "",
        entityUuid: "",
        includeLiveContents,
        isMagazinesStyle: appBarVariant === "scmp/magazines-style",
        isVideo: false,
      };
};

const ArticlePageAtomInitializer: FunctionComponent = () => {
  const router = useRouter();
  const variables = useMemo(() => getVariables(router), [router]);
  const { article, videoContent } = useLazyLoadQuery<articlePageQuery>(query, variables, {
    // This initializer runs outside of a Suspense boundary during hydration, so it
    // must never suspend. store-only never suspends: on initial page load the
    // store is hydrated by relay-nextjs before the first render.
    fetchPolicy: "store-only",
  });

  useHydrateAtoms([[currentItemAtom, getCurrentItemData(article ?? videoContent)]]);

  return null;
};

const ArticlePageWithRelay: RelayComponentWithLayout<
  PageProps & { aiSeoOverride?: AiSeoOverride },
  articlePageQuery
> = withRelay(ArticlePage, query, {
  clientSideProps: context => getPageProps(context),
  createClientEnvironment: () => createClientEnvironment(),
  createServerEnvironment: async context => {
    const { createServerEnvironment } = await import("scmp-app/lib/relay/environment.server");
    return createServerEnvironment(context.req?.cookies);
  },
  fallback: <FallbackLoading />,
  serverSideProps: async context => {
    const pageProps = getPageProps(context);

    const entityId = context.query.entityId?.toString() ?? "";
    const entityUuid = context.query.entityUuid?.toString() ?? "";

    const isBot = isBotRequest(context);

    // AI SEO is gated by: AI_SEO_ENABLED kill switch, bot traffic, entity ID threshold,
    // a 50% A/B rollout (even entityId = variant, odd = control), and an optional
    // section whitelist (AI_SEO_WHITELIST_SECTION_IDS). Empty whitelist = all sections.
    const numericEntityId = Number.isFinite(Number(entityId)) ? Number(entityId) : 0;
    const isVariant = numericEntityId % 2 === 0;
    const isAboveThreshold = numericEntityId > serverConfig.aiSeo.minEntityId;

    const sectionIds =
      typeof context.query.sectionIds === "string"
        ? context.query.sectionIds.split(",").filter(Boolean)
        : [];
    const whitelist = serverConfig.aiSeo.whitelistSectionIds;
    const isWhitelisted = whitelist.length === 0 || sectionIds.some(id => whitelist.includes(id));

    let aiSeoOverride: AiSeoOverride | undefined;

    if (
      serverConfig.aiSeo.enabled &&
      isBot &&
      isAboveThreshold &&
      isVariant &&
      isWhitelisted &&
      entityUuid
    ) {
      aiSeoOverride = await fetchAiSeoSuggestion(entityUuid);
    }

    return { ...pageProps, aiSeoOverride };
  },
  variablesFromContext: getVariables,
});

ArticlePageWithRelay.pageAtomInitializer = ArticlePageAtomInitializer;

export default ArticlePageWithRelay;
